import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Volume2, VolumeX } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LETTERS = [
  { c: 'ا', uz: 'Alif', ru: 'Алиф', en: 'Alef' },
  { c: 'ب', uz: 'Ba', ru: 'Ба', en: 'Ba' },
  { c: 'ت', uz: 'Ta', ru: 'Та', en: 'Ta' },
  { c: 'ث', uz: 'Sa', ru: 'Са', en: 'Tha' },
  { c: 'ج', uz: 'Jim', ru: 'Джим', en: 'Jeem' },
  { c: 'ح', uz: 'Ha', ru: 'Ха', en: 'Ha' },
  { c: 'خ', uz: 'Xa', ru: 'Ха', en: 'Kha' },
  { c: 'د', uz: 'Dol', ru: 'Даль', en: 'Dal' },
  { c: 'ذ', uz: 'Zol', ru: 'Заль', en: 'Dhal' },
  { c: 'ر', uz: 'Ro', ru: 'Ра', en: 'Ra' },
  { c: 'ز', uz: 'Za', ru: 'Зайн', en: 'Zay' },
  { c: 'س', uz: 'Sin', ru: 'Син', en: 'Seen' },
  { c: 'ش', uz: 'Shin', ru: 'Шин', en: 'Sheen' },
  { c: 'ص', uz: 'Sod', ru: 'Сад', en: 'Sad' },
  { c: 'ض', uz: 'Dod', ru: 'Дад', en: 'Dad' },
  { c: 'ط', uz: 'To', ru: 'Та', en: 'Ta' },
  { c: 'ظ', uz: 'Zo', ru: 'За', en: 'Zah' },
  { c: 'ع', uz: 'Ayn', ru: 'Айн', en: 'Ayn' },
  { c: 'غ', uz: "G'ayn", ru: 'Гайн', en: 'Ghayn' },
  { c: 'ف', uz: 'Fo', ru: 'Фа', en: 'Fa' },
  { c: 'ق', uz: 'Qof', ru: 'Каф', en: 'Qaf' },
  { c: 'ك', uz: 'Kof', ru: 'Кяф', en: 'Kaf' },
  { c: 'ل', uz: 'Lom', ru: 'Лям', en: 'Lam' },
  { c: 'م', uz: 'Mim', ru: 'Мим', en: 'Meem' },
  { c: 'ن', uz: 'Nun', ru: 'Нун', en: 'Noon' },
  { c: 'ه', uz: 'Ha', ru: 'Ха', en: 'Ha' },
  { c: 'و', uz: 'Vov', ru: 'Вав', en: 'Waw' },
  { c: 'ي', uz: 'Yo', ru: 'Йа', en: 'Ya' },
];

// pitch shift only (tempo stays natural) — cents of detune applied to the one voice we have,
// so "male"/"female" sound like two distinct voices instead of one fixed voice
const GENDER_DETUNE = { male: -280, female: 380 };

const TXT = {
  uz: {
    title: 'Harflar tovushi', sub: "Har bir harfni bosing va uning arabcha talaffuzini tinglang.",
    failed: 'Ovoz yuklab bo’lmadi — internet aloqangizni tekshirib, qaytadan bosib ko’ring.',
    male: 'Erkak ovoz', female: 'Ayol ovoz',
  },
  ru: {
    title: 'Звуки букв', sub: 'Нажмите на букву, чтобы услышать её произношение на арабском.',
    failed: 'Не удалось загрузить звук — проверьте интернет-соединение и попробуйте снова.',
    male: 'Мужской голос', female: 'Женский голос',
  },
  en: {
    title: 'Letter Sounds', sub: 'Tap a letter to hear its Arabic pronunciation.',
    failed: 'Couldn’t load the sound — check your internet connection and try again.',
    male: 'Male voice', female: 'Female voice',
  },
};

function LetterSounds() {
  const { i18n } = useTranslation();
  const lang = ['uz', 'ru', 'en'].includes(i18n.language) ? i18n.language : 'uz';
  const t = TXT[lang];
  const [playingIdx, setPlayingIdx] = useState(null);
  const [failedIdx, setFailedIdx] = useState(null);
  const [gender, setGender] = useState('male');
  const ctxRef = useRef(null);
  const bufferCacheRef = useRef(new Map()); // letter -> decoded AudioBuffer, reused across replays/genders
  const sourceRef = useRef(null); // currently playing node, so a new click can cut it off

  const getContext = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  };

  const speak = async (letter, idx) => {
    setFailedIdx(null);
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (_) {}
      sourceRef.current = null;
    }
    try {
      const ctx = getContext();
      let buffer = bufferCacheRef.current.get(letter.c);
      if (!buffer) {
        const res = await fetch(`${API_URL}/tts?q=${encodeURIComponent(letter.c)}&lang=ar`);
        if (!res.ok) throw new Error('bad response');
        const arrayBuffer = await res.arrayBuffer();
        buffer = await ctx.decodeAudioData(arrayBuffer);
        bufferCacheRef.current.set(letter.c, buffer);
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.detune.value = GENDER_DETUNE[gender];
      source.connect(ctx.destination);
      source.onended = () => {
        setPlayingIdx((cur) => (cur === idx ? null : cur));
        if (sourceRef.current === source) sourceRef.current = null;
      };
      sourceRef.current = source;
      source.start();
      setPlayingIdx(idx);
    } catch (err) {
      setPlayingIdx(null);
      setFailedIdx(idx);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-semibold mb-2 text-stone-800 dark:text-stone-100">{t.title}</h1>
      <p className="text-stone-500 dark:text-stone-400 mb-6">{t.sub}</p>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => setGender('male')}
          className={`px-4 py-2 rounded-full font-semibold border-2 transition-all ${
            gender === 'male'
              ? 'bg-brand-700 text-white border-brand-700'
              : 'bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-stone-700 hover:border-brand-300'
          }`}
        >
          {t.male}
        </button>
        <button
          type="button"
          onClick={() => setGender('female')}
          className={`px-4 py-2 rounded-full font-semibold border-2 transition-all ${
            gender === 'female'
              ? 'bg-brand-700 text-white border-brand-700'
              : 'bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-stone-700 hover:border-brand-300'
          }`}
        >
          {t.female}
        </button>
      </div>

      {failedIdx !== null && (
        <div className="mb-6 bg-red-50 dark:bg-red-950/40 border-l-4 border-red-400 text-red-800 dark:text-red-300 px-4 py-3 rounded-xl">
          {t.failed}
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-4">
        {LETTERS.map((letter, idx) => (
          <button
            key={letter.c}
            type="button"
            onClick={() => speak(letter, idx)}
            className={`flex flex-col items-center gap-1 p-4 rounded-2xl border-2 bg-white dark:bg-stone-900 transition-all hover:shadow-md ${
              playingIdx === idx
                ? 'border-brand-400 ring-4 ring-brand-100 dark:ring-brand-900/40'
                : failedIdx === idx
                ? 'border-red-400'
                : 'border-stone-200 dark:border-stone-800'
            }`}
          >
            <span className="font-arabic text-4xl text-stone-800 dark:text-stone-100">{letter.c}</span>
            <span className="text-xs font-semibold text-stone-500 dark:text-stone-400">{letter[lang]}</span>
            {failedIdx === idx ? (
              <VolumeX size={16} className="text-red-400" />
            ) : (
              <Volume2 size={16} className={playingIdx === idx ? 'animate-pulse text-brand-600' : 'text-stone-300 dark:text-stone-600'} />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default LetterSounds;
